import {
  Badge,
  Button,
  Checkbox,
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@devhop/ui";
import { PrinterIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import type { AssignmentDTO, EventShowDTO } from "../../domain/contracts";
import {
  buildShowAssignmentMatrix,
  toggleShowForPerformer,
} from "../../domain/services/show-assignment-matrix";
import { performanceApi } from "../api/client";

type ShowAssignmentMatrixProps = {
  eventId: string;
  assignments: AssignmentDTO[];
  eventShows: EventShowDTO[];
  canManage: boolean;
  submitting?: boolean;
  onBulkUpdate: (
    updates: Array<{ assignmentId: string; eventShowIds: string[] }>,
  ) => Promise<void>;
  onAddShowToAll?: (eventShowId: string) => Promise<void>;
};

export function ShowAssignmentMatrix({
  eventId,
  assignments,
  eventShows,
  canManage,
  submitting = false,
  onBulkUpdate,
  onAddShowToAll,
}: ShowAssignmentMatrixProps) {
  const model = useMemo(
    () => buildShowAssignmentMatrix(assignments, eventShows),
    [assignments, eventShows],
  );
  const [pendingCell, setPendingCell] = useState<string | null>(null);

  if (model.columns.length < 2 || model.rows.length === 0) return null;

  const printUrl = performanceApi.events.showRosterPrintUrl(eventId);

  const handleToggle = async (
    performerId: string,
    showId: string,
    nextLinked: boolean,
  ) => {
    const row = model.rows.find((item) => item.performerId === performerId);
    if (!row) return;
    const updates = toggleShowForPerformer(row.assignments, showId, nextLinked);
    if (updates.length === 0) return;
    const cellKey = `${performerId}:${showId}`;
    setPendingCell(cellKey);
    try {
      await onBulkUpdate(updates);
    } finally {
      setPendingCell(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-sm">ຕາຕະລາງນັກສະແດງ × ໂຊ</p>
          <p className="text-muted-foreground text-xs">
            ກົດ checkbox ເພື່ອເພີ່ມ/ຖອນນັກສະແດງອอกຈາກໂຊ
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => window.open(printUrl, "_blank", "noopener,noreferrer")}
          >
            <PrinterIcon className="size-4" />
            ພິມ roster
          </Button>
          {canManage && onAddShowToAll && model.columns.length > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={submitting}
              onClick={() => {
                const latestShow = model.columns[model.columns.length - 1];
                if (latestShow) void onAddShowToAll(latestShow.showId);
              }}
            >
              ເພີ່ມໂຊໃໝ່ໃຫ້ທຸກຄົນ
            </Button>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <TooltipProvider delayDuration={150}>
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="sticky left-0 z-10 min-w-40 bg-muted/30 px-3 py-2.5 text-left font-medium text-muted-foreground text-xs">
                  ນັກສະແດງ
                </th>
                {model.columns.map((column) => (
                  <th
                    key={column.showId}
                    className="min-w-[7.5rem] border-b px-2 py-2.5 text-center font-medium text-muted-foreground text-xs"
                  >
                    <div>{column.label}</div>
                    <div className="font-normal">
                      {formatDateTimeLocal(column.showDate)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.performerId} className="border-b last:border-b-0">
                  <td className="sticky left-0 z-10 bg-card px-3 py-2.5 font-medium">
                    {row.performerName}
                  </td>
                  {row.cells.map((cell) => {
                    const cellKey = `${row.performerId}:${cell.showId}`;
                    const busy = pendingCell === cellKey || submitting;
                    const disabled = !canManage || busy;
                    return (
                      <td key={cell.showId} className="p-1.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              disabled={disabled}
                              aria-pressed={cell.linked}
                              aria-label={
                                cell.linked
                                  ? `${row.performerName} — ຖອນອອກຈາກໂຊ`
                                  : `${row.performerName} — ເພີ່ມເຂົ້າໂຊ`
                              }
                              className={cn(
                                "flex h-12 w-full min-w-[3.25rem] cursor-pointer items-center justify-center rounded-lg border transition-colors",
                                cell.linked
                                  ? "border-primary bg-primary/10 hover:bg-primary/15"
                                  : "border-dashed border-muted-foreground/30 bg-muted/15 hover:bg-muted/35",
                                disabled && "cursor-not-allowed opacity-60",
                              )}
                              onClick={() => {
                                if (disabled) return;
                                void handleToggle(
                                  row.performerId,
                                  cell.showId,
                                  !cell.linked,
                                );
                              }}
                            >
                              <Checkbox
                                checked={cell.linked}
                                tabIndex={-1}
                                aria-hidden
                                className="pointer-events-none size-5"
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {cell.linked ? "ເຂົ້າຮ່ວມໂຊນີ້" : "ບໍ່ເຂົ້າຮ່ວມ"}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </TooltipProvider>
      </div>
    </div>
  );
}

export function PartialShowParticipationReport({
  rows,
  eventShows,
}: {
  rows: Array<{
    performerId: string;
    performerName: string;
    selectedCount: number;
    totalShowCount: number;
    eventShowIds: string[];
  }>;
  eventShows: EventShowDTO[];
}) {
  if (rows.length === 0) return null;

  const showLabel = (showId: string) => {
    const index = eventShows.findIndex((show) => show.id === showId);
    if (index < 0) return showId;
    return `ໂຊ ${index + 1}`;
  };

  return (
    <div className="rounded-lg border bg-muted/10 px-4 py-3">
      <p className="mb-2 font-medium text-sm">ລາຍງານ: ເຂົ້າຮ່ວມບາງໂຊ</p>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.performerId}
            className="flex flex-wrap items-center gap-2 text-sm"
          >
            <span className="font-medium">{row.performerName}</span>
            <Badge variant="outline">
              {row.selectedCount}/{row.totalShowCount} ໂຊ
            </Badge>
            <span className="text-muted-foreground text-xs">
              {row.eventShowIds.map(showLabel).join(" · ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
